import { computed, Injectable, signal } from '@angular/core';
import { Subject } from 'rxjs';
import { AlertType } from '../../components/alert/alert';

export interface NotificationItem {
  id: string;
  type: AlertType;
  message: string;
  timestamp: Date;
  read: boolean;
}

@Injectable({
  providedIn: 'root',
})
export class NotificationService {
  private messageSubject = new Subject<{ severity: string; summary: string; detail: string }>();
  messages$ = this.messageSubject.asObservable();

  notify(severity: string, summary: string, detail: string) {
    this.messageSubject.next({ severity, summary, detail });
    const validTypes: AlertType[] = ['success', 'error', 'warning', 'info'];
    const type = validTypes.includes(severity as AlertType) ? (severity as AlertType) : 'info';
    this.show(type, detail || summary);
  }

  notifyError(summary: string, detail: string) {
    this.messageSubject.next({ severity: 'error', summary, detail });
    this.show('error', detail || summary);
  }

  private readonly _notifications = signal<NotificationItem[]>([]);
  readonly notifications = this._notifications.asReadonly();
  readonly unreadCount = computed(() => this._notifications().filter(n => !n.read).length);

  private readonly _newNotification = new Subject<NotificationItem>();
  readonly newNotification$ = this._newNotification.asObservable();

  show(type: AlertType, message: string): void {
    const item: NotificationItem = {
      id: crypto.randomUUID(),
      type,
      message,
      timestamp: new Date(),
      read: false,
    };
    this._notifications.update(list => [item, ...list].slice(0, 20));
    this._newNotification.next(item);
  }

  markAllRead(): void {
    this._notifications.update(list => list.map(n => ({ ...n, read: true })));
  }

  clearAll(): void {
    this._notifications.set([]);
  }
}
